import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Twitter } from "lucide-react";
import Navbar from "../components/Navbar";
import PageMeta from "../components/PageMeta";
import Footer from "../components/Footer";
import FloatingFAQ from "../components/FloatingFAQ";

const team = [
  {
    name: "Arya Javaherzadeh",
    role: "CEO / Founder",
    bio: "Visionary leader building digital solutions for businesses.",
    image: "/assets/team/arya.png"
  }
];

const stats = [
  { number: "50+", label: "Projects Delivered" },
  { number: "98%", label: "Client Satisfaction" },
  { number: "5+", label: "Years Experience" },
  { number: "24/7", label: "Support Available" }
];

export default function About() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="About Us | Averoan Technologies"
        description="Meet the team behind Averoan Technologies, a Vancouver digital agency helping businesses grow online since 2021."
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 relative" data-testid="about-hero">
        <div className="hero-glow absolute top-0 right-0 opacity-30" />
        <div className="container-main relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="tag mb-6">About Us</span>
              <h1 
                className="text-5xl md:text-7xl font-bold mb-6"
                style={{ fontFamily: '"Clash Display", sans-serif' }}
              >
                We're a team of<br />
                <span style={{ color: '#D4FF00' }}>creators</span>
              </h1>
              <p 
                className="text-base md:text-lg max-w-lg"
                style={{ color: '#A1A1AA' }}
              >
                We help businesses grow through strategic design, development, and marketing. 
                Our mission is to create digital experiences that make a real impact.
              </p>
            </div>
            
            <div className="relative">
              <div 
                className="absolute -inset-4 rounded-2xl opacity-50"
                style={{ background: 'linear-gradient(45deg, rgba(212,255,0,0.1) 0%, transparent 50%)' }}
              />
              <img 
                src="/assets/hero/about-team.jpg"
                alt="Team working"
                className="w-full rounded-xl relative z-10"
                style={{ filter: 'grayscale(30%)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16" style={{ background: '#18181B' }} data-testid="about-stats">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={stat.label} className="text-center">
                <div 
                  className="text-4xl md:text-5xl font-bold mb-2"
                  style={{ fontFamily: '"Clash Display", sans-serif', color: '#D4FF00' }}
                >
                  {stat.number}
                </div>
                <div className="text-sm" style={{ color: '#A1A1AA' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding" data-testid="about-team">
        <div className="container-main">
          <div className="mb-12">
            <span className="tag mb-4">Our Team</span>
            <h2 
              className="text-4xl md:text-6xl font-bold mt-4"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Meet the crew
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={member.name}
                className="group"
                data-testid={`team-member-${index}`}
              >
                <div className="overflow-hidden rounded-xl mb-4 relative">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full aspect-[3/4] object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    style={{ filter: 'grayscale(50%)' }}
                  />
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: 'linear-gradient(to top, rgba(212,255,0,0.2) 0%, transparent 50%)' }}
                  />
                </div>
                <h3 
                  className="text-xl font-bold mb-1 normal-case"
                  style={{ fontFamily: '"Clash Display", sans-serif' }}
                >
                  {member.name}
                </h3>
                <p className="text-sm mb-2" style={{ color: '#D4FF00' }}>{member.role}</p>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: '#18181B' }} data-testid="about-cta">
        <div className="container-main">
          <div className="text-center max-w-2xl mx-auto">
            <h2 
              className="text-4xl md:text-6xl font-bold mb-6"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Want to<br />
              <span style={{ color: '#D4FF00' }}>join us?</span>
            </h2>
            <p className="text-base md:text-lg mb-10" style={{ color: '#A1A1AA' }}>
              We're always looking for talented individuals who share our passion for great work.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="about-cta-btn">
              Get in Touch <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
}
